import React, { useCallback, useEffect, useState } from "react";
import {
  getAllDocuments,
  deleteDocument,
} from "../../../api/elearning/documents/documents";
import DocumentFilter from "./DocumentFilter";
import DocumentCreateDrawer from "./DocumentCreateModal";
import DocumentEditModal from "./DocumentEditModal";
import DocumentDetailModal from "./DocumentDetailModal";
import DocumentPdfPreviewModal from "./DocumentPdfPreviewModal";
import DocumentMiniDashboard, {
  formatDocFileSize,
} from "./DocumentMiniDashboard";
import Pagination from "../../../components/common/Pagination";
import { isPdfDocument } from "./documentViewUtils";
import {
  showSuccessToast,
  showErrorToast,
  showInfoToast,
} from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile } from "@fortawesome/free-solid-svg-icons";
import { hasPermission } from "../../../utils/permissionUtils";
import {
  Sparkles,
  CircleHelp,
  FilePlus2,
  LayoutGrid,
  List,
  FileText,
  Film,
  Archive,
  FileQuestion,
  ChevronDown,
  X,
} from "lucide-react";

import {
  subscribeDocumentUploads,
  startDocumentUploads,
  clearDocumentUploads,
  cancelDocumentUpload,
  removeDocumentUploadJob,
} from "./documentUploadManager";
import { startEduDocumentsPageTour } from "./eduDocumentsPageTour";
import { startEduDocumentCreateTour } from "./eduDocumentCreateTour";


// Inline SVG components tránh lỗi "cloud file provider is not running" của OneDrive
const Spinner = ({ className = "h-4 w-4 animate-spin" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const CheckCircleIcon = ({ className = "h-4 w-4" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const AlertCircleIcon = ({ className = "h-4 w-4" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const VIEW_MODE_KEY = "ima_pwa_edu_documents_view_mode";
const DEFAULT_PAGE_SIZE = 20;
function readStoredViewMode() {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY);
    if (v === "list" || v === "card") return v;
  } catch {
    /* ignore */
  }
  return "card";
}

const LEVEL_LABELS = {
  mam_non: "Mầm non",
  tieu_hoc: "Tiểu học",
  thcs: "THCS",
  thpt: "THPT",
};

const TYPE_LABELS = {
  giaotrinh: "Giáo trình",
  giao_trinh: "Giáo trình",
  baigiang: "Bài giảng",
  bai_giang: "Bài giảng",
  baitap: "Bài tập",
  bai_tap: "Bài tập",
  video: "Video bài học",
  dethi: "Đề kiểm tra",
  de_thi: "Đề kiểm tra",
  khac: "Khác",
};

const TYPE_SUBJECTS = {
  ky_nang_song: "Kỹ Năng Sống",
  toan_tu_duy: "Toán Tư Duy",
  tieng_anh_tang_cuong: "Tiếng Anh Tăng Cường",
};

function extVisual(ext) {
  const e = String(ext || "")
    .trim()
    .toLowerCase()
    .replace(/^\./, "");
  if (e === "pdf") {
    return {
      Icon: FileText,
      wrap: "from-red-600 to-red-800",
      chip: "bg-red-100 text-red-800 border-red-200",
      label: "PDF",
    };
  }
  if (e === "zip" || e === "rar" || e === "7z") {
    return {
      Icon: Archive,
      wrap: "from-amber-500 to-orange-700",
      chip: "bg-amber-100 text-amber-900 border-amber-200",
      label: e.toUpperCase(),
    };
  }
  if (["mp4", "webm", "mov", "mkv"].includes(e)) {
    return {
      Icon: Film,
      wrap: "from-violet-600 to-indigo-800",
      chip: "bg-violet-100 text-violet-900 border-violet-200",
      label: e.toUpperCase(),
    };
  }
  return {
    Icon: FileQuestion,
    wrap: "from-slate-500 to-slate-700",
    chip: "bg-slate-100 text-slate-700 border-slate-200",
    label: e ? e.toUpperCase() : "FILE",
  };
}

function subjectLabel(doc) {
  return TYPE_SUBJECTS[doc.subject] || doc.subject || "—";
}

function courseMeta(doc) {
  return (
    doc.course_name ||
    LEVEL_LABELS[doc.level] ||
    doc.level ||
    "—"
  );
}

function typeLabel(doc) {
  return TYPE_LABELS[doc.type] || doc.type || "—";
}

const DocumentCardPage = () => {
  const [documents, setDocuments] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [detailDoc, setDetailDoc] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState(readStoredViewMode);

  const [uploadJobs, setUploadJobs] = useState([]);
  const [autoDismissLeft, setAutoDismissLeft] = useState(null);
  const [isUploadMinimized, setIsUploadMinimized] = useState(false);

  const [queuedTour, setQueuedTour] = useState(null);
  const canQuickTour = hasPermission("elearning.documents", "R");

  const changeViewMode = useCallback((mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, []);

  const reloadDocuments = useCallback(
    async (overrides = {}) => {
      const page = overrides.page ?? currentPage;
      const size = overrides.size ?? pageSize;
      const filters = overrides.filters ?? filterValues;
      setLoading(true);
      try {
        const data = await getAllDocuments({
          page,
          size,
          keyword: filters.keyword || undefined,
          subject: filters.subject || undefined,
          type: filters.type || undefined,
          extension: filters.extension || undefined,
          is_active: filters.is_active || undefined,
        });
        setDocuments(data.items || []);
        setTotalRecords(Number(data.total) || 0);
        setDashboardStats(data.stats || null);
        setCurrentPage(Number(data.page) || page);
      } catch (err) {
        console.error("Lỗi khi tải danh sách tài liệu:", err);
        showErrorToast(
          err.response?.data?.message || "Không thể tải danh sách tài liệu."
        );
        setDocuments([]);
        setTotalRecords(0);
        setDashboardStats(null);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, pageSize, filterValues]
  );

  const startUploads = ({ files, meta }) =>
    startDocumentUploads({ files, meta });

  const handlePageTour = () => {
    window.setTimeout(() => startEduDocumentsPageTour(), 80);
  };

  const handleCreateTour = () => {
    setQueuedTour("create");
    setOpenDrawer(true);
  };

  const openGuide = () => {
    const guideUrl = `${window.location.origin}/guides/elearning-edu-documents-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    reloadDocuments({ page: currentPage, size: pageSize, filters: filterValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filterValues]);

  useEffect(() => {
    const handleUploadSuccess = () => {
      reloadDocuments();
    };
    window.addEventListener("ima:document_upload_success", handleUploadSuccess);
    return () => {
      window.removeEventListener("ima:document_upload_success", handleUploadSuccess);
    };
  }, [reloadDocuments]);

  const handleFilterChange = (next) => {
    setFilterValues(next || {});
    setCurrentPage(1);
  };
  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  useEffect(() => {
    const unsub = subscribeDocumentUploads(setUploadJobs);
    return () => unsub();
  }, []);

  useEffect(() => {
    if (queuedTour === "create" && openDrawer) {
      window.setTimeout(() => {
        startEduDocumentCreateTour();
        setQueuedTour(null);
      }, 220);
    }
  }, [queuedTour, openDrawer]);

  useEffect(() => {
    if (!uploadJobs.length) {
      setAutoDismissLeft(null);
      return;
    }

    const allFinished = uploadJobs.every(
      (j) => j.status === "done" || j.status === "error"
    );
    if (!allFinished) {
      setAutoDismissLeft(null);
      return;
    }

    const AUTO_DISMISS_SEC = 10;
    if (autoDismissLeft === null) setAutoDismissLeft(AUTO_DISMISS_SEC);
  }, [uploadJobs, autoDismissLeft]);

  useEffect(() => {
    if (autoDismissLeft === null) return;
    if (autoDismissLeft <= 0) {
      clearDocumentUploads();
      return;
    }

    const t = window.setTimeout(
      () => setAutoDismissLeft((s) => (s == null ? null : s - 1)),
      1000
    );
    return () => window.clearTimeout(t);
  }, [autoDismissLeft]);

  const handleView = (doc) => {
    if (!isPdfDocument(doc)) {
      showInfoToast(
        "Chỉ tài liệu PDF được xem trước trong ứng dụng. Định dạng này không hỗ trợ xem trực tiếp tại đây."
      );
      return;
    }
    setPreviewDoc(doc);
  };

  const handleDeleteDocument = async (doc) => {
    const confirm = await confirmDelete({
      type: DELETE_ENTITY.document,
      name: doc?.file_name || doc?.name,
    });

    if (!confirm.isConfirmed) return;

    try {
      await deleteDocument(doc.id);
      showSuccessToast("Tài liệu đã được xóa.");
      setDetailDoc(null);
      const nextTotal = Math.max(0, totalRecords - 1);
      const maxPage = Math.max(1, Math.ceil(nextTotal / pageSize) || 1);
      const nextPage = Math.min(currentPage, maxPage);
      if (nextPage !== currentPage) setCurrentPage(nextPage);
      else reloadDocuments({ page: nextPage });
    } catch (error) {
      console.error("Xóa tài liệu thất bại:", error);
      showErrorToast(
        error.response?.data?.message || "Không thể xóa tài liệu."
      );
    }
  };

  const openDetail = (doc) => setDetailDoc(doc);

  return (
    <div className="p-4">
      <div
        className="mb-8 flex items-center justify-between gap-3"
        data-tour="edu-documents-header"
      >
        <div className="flex min-w-0 items-center gap-4">
          <div className="shrink-0 rounded-2xl bg-gradient-to-br from-red-600 to-red-800 p-2.5 text-white shadow-lg shadow-red-300/50">
            <FontAwesomeIcon
              icon={faFile}
              className="flex h-6 w-6 items-center justify-center text-xl"
            />
          </div>

          <div className="min-w-0">
            <h1 className="text-xl font-black uppercase tracking-tight text-stone-900 sm:text-2xl">
              Quản lý <span className="text-red-700">tài liệu học tập</span>
            </h1>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
              <p className="text-nowrap text-[11px] font-bold uppercase tracking-wider text-stone-500">
                <span className="text-red-600">{totalRecords}</span> tài liệu
                {loading ? (
                  <span className="ml-2 inline-flex items-center gap-1 normal-case tracking-normal text-stone-400">
                    <Spinner className="h-3 w-3 animate-spin" /> đang tải…
                  </span>
                ) : null}
              </p>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <div
            className="inline-flex h-11 items-center rounded-2xl border border-stone-200 bg-white p-1 shadow-sm"
            role="group"
            aria-label="Kiểu hiển thị"
            data-tour="edu-documents-view-mode"
          >
            <button
              type="button"
              title="Dạng thẻ"
              aria-pressed={viewMode === "card"}
              onClick={() => changeViewMode("card")}
              className={`inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-xs font-bold transition-colors ${
                viewMode === "card"
                  ? "bg-red-700 text-white shadow-sm"
                  : "text-stone-500 hover:bg-stone-50 hover:text-red-700"
              }`}
            >
              <LayoutGrid size={16} strokeWidth={2.25} />
              <span className="hidden sm:inline">Thẻ</span>
            </button>
            <button
              type="button"
              title="Dạng danh sách"
              aria-pressed={viewMode === "list"}
              onClick={() => changeViewMode("list")}
              className={`inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-xs font-bold transition-colors ${
                viewMode === "list"
                  ? "bg-red-700 text-white shadow-sm"
                  : "text-stone-500 hover:bg-stone-50 hover:text-red-700"
              }`}
            >
              <List size={16} strokeWidth={2.25} />
              <span className="hidden sm:inline">Danh sách</span>
            </button>
          </div>

          {canQuickTour ? (
            <div
              className="flex items-center gap-2"
              data-tour="edu-documents-header-guides"
            >
              <HeaderIconButton
                icon={Sparkles}
                label="Hướng dẫn nhanh"
                onClick={handlePageTour}
              />
              <HeaderIconButton
                icon={FilePlus2}
                label="Tour thêm tài liệu"
                onClick={handleCreateTour}
              />
              <HeaderIconButton
                icon={CircleHelp}
                label="Hướng dẫn sử dụng"
                onClick={openGuide}
              />
            </div>
          ) : null}
        </div>
      </div>

      <div data-tour="edu-documents-dashboard">
        <DocumentMiniDashboard documents={documents} stats={dashboardStats} />
      </div>

      <div data-tour="edu-documents-filter">
        <DocumentFilter
          onNewClick={() => setOpenDrawer(true)}
          onFilterChange={handleFilterChange}
        />
      </div>

      {loading && documents.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-stone-500">
          <Spinner className="h-5 w-5 animate-spin text-red-600" /> Đang tải tài liệu…
        </div>
      ) : documents.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 py-16 text-center text-stone-500">
          <p className="font-medium text-stone-700">Không có tài liệu phù hợp.</p>
          <p className="mt-1 text-sm">
            Thử đổi bộ lọc hoặc tải lên tài liệu mới.
          </p>
        </div>
      ) : (
        <div
          className={
            viewMode === "list"
              ? "mt-2 flex w-full flex-col gap-3"
              : "mt-2 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
          }
          data-tour="edu-documents-grid"
        >
          {documents.map((doc) =>
            viewMode === "list" ? (
              <DocumentListRow
                key={doc.id}
                doc={doc}
                onOpen={() => openDetail(doc)}
              />
            ) : (
              <DocumentCard
                key={doc.id}
                doc={doc}
                onOpen={() => openDetail(doc)}
              />
            )
          )}
        </div>
      )}

      {totalRecords > 0 ? (
        <Pagination
          totalRecords={totalRecords}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      ) : null}

      <DocumentCreateDrawer
        isOpen={openDrawer}
        onClose={() => {
          setOpenDrawer(false);
          setQueuedTour(null);
        }}
        onSuccess={() => {
          setCurrentPage(1);
          reloadDocuments({ page: 1 });
        }}
        onStartUploads={startUploads}
      />

      <DocumentEditModal
        isOpen={!!editingDoc}
        document={editingDoc}
        onClose={() => setEditingDoc(null)}
        onSaved={() => reloadDocuments()}
      />

      {previewDoc ? (
        <DocumentPdfPreviewModal
          document={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      ) : null}

      {detailDoc ? (
        <DocumentDetailModal
          document={detailDoc}
          onClose={() => setDetailDoc(null)}
          onView={() => handleView(detailDoc)}
          onEdit={() => {
            setEditingDoc(detailDoc);
            setDetailDoc(null);
          }}
          onDelete={() => handleDeleteDocument(detailDoc)}
          canView={hasPermission("elearning.documents", "R")}
          canEdit={hasPermission("elearning.documents", "U")}
          canDelete={hasPermission("elearning.documents", "D")}
        />
      ) : null}

      {uploadJobs.length > 0 ? (
        isUploadMinimized ? (
          /* Mini bar góc dưới phải (Google Drive style khi thu nhỏ) */
          <div
            className="fixed bottom-5 right-5 z-[250] flex w-[350px] cursor-pointer items-center justify-between rounded-2xl border border-gray-200 bg-white p-3 shadow-2xl transition hover:border-red-300"
            onClick={() => setIsUploadMinimized(false)}
            data-tour="edu-documents-upload-queue"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              {uploadJobs.some((j) => j.status === "uploading" || j.status === "queued") ? (
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
                  <Spinner className="h-4 w-4 animate-spin" />
                </div>
              ) : uploadJobs.some((j) => j.status === "error") ? (
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                  <AlertCircleIcon className="h-4 w-4" />
                </div>
              ) : (
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                  <CheckCircleIcon className="h-4 w-4" />
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate text-xs font-bold text-gray-800">
                  {uploadJobs.some((j) => j.status === "uploading" || j.status === "queued")
                    ? `Đang tải lên ${uploadJobs.filter((j) => j.status === "uploading" || j.status === "queued").length} tệp...`
                    : uploadJobs.some((j) => j.status === "error")
                      ? `Có ${uploadJobs.filter((j) => j.status === "error").length} tệp lỗi`
                      : `Đã tải lên ${uploadJobs.length} tệp hoàn tất`}
                </p>
                <p className="text-[10px] font-medium text-gray-500">
                  {uploadJobs.filter((j) => j.status === "done").length}/{uploadJobs.length} hoàn tất · Bấm để xem chi tiết
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsUploadMinimized(false);
                }}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                title="Mở rộng"
              >
                <ChevronDown className="h-4 w-4 rotate-180" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  clearDocumentUploads();
                  setAutoDismissLeft(null);
                  reloadDocuments();
                }}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                title="Đóng tiến trình"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          /* Khung chi tiết nổi góc dưới phải (Google Drive style khi mở rộng) */
          <div
            className="fixed bottom-5 right-5 z-[250] w-[420px] max-w-[calc(100vw-24px)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl transition"
            data-tour="edu-documents-upload-queue"
          >
            {/* Header thanh task */}
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white px-4 py-3">
              <div className="flex min-w-0 items-center gap-2.5">
                {uploadJobs.some((j) => j.status === "uploading" || j.status === "queued") ? (
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-600">
                    <Spinner className="h-4 w-4 animate-spin" />
                  </div>
                ) : uploadJobs.some((j) => j.status === "error") ? (
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                    <AlertCircleIcon className="h-4 w-4" />
                  </div>
                ) : (
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                    <CheckCircleIcon className="h-4 w-4" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="truncate text-xs font-black text-gray-800">
                    {uploadJobs.some((j) => j.status === "uploading" || j.status === "queued")
                      ? `Đang tải lên ${uploadJobs.filter((j) => j.status === "uploading" || j.status === "queued").length} tệp...`
                      : uploadJobs.some((j) => j.status === "error")
                        ? "Tải lên có lỗi"
                        : "Tải lên hoàn tất"}
                  </div>
                  <div className="mt-0.5 text-[10px] font-medium text-gray-500">
                    {uploadJobs.filter((j) => j.status === "done").length}/{uploadJobs.length} hoàn tất
                    {autoDismissLeft ? ` · Tự đóng sau ${autoDismissLeft}s` : ""}
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => setIsUploadMinimized(true)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  title="Thu nhỏ"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    clearDocumentUploads();
                    setAutoDismissLeft(null);
                    reloadDocuments();
                  }}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  title="Đóng"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Danh sách từng tệp tải lên */}
            <div className="max-h-[350px] divide-y divide-gray-100 overflow-y-auto p-3">
              {uploadJobs.map((job) => {
                const isZip = String(job.ext || job.fileName).toLowerCase().endsWith(".zip") || job.docType === "bai_giang";
                const isVideo = String(job.ext || job.fileName).toLowerCase().endsWith(".mp4") || job.docType === "video";
                const isPdf = String(job.ext || job.fileName).toLowerCase().endsWith(".pdf");

                return (
                  <div key={job.id} className="py-2.5 first:pt-1 last:pb-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-2.5">
                        <div
                          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                            isZip
                              ? "bg-amber-50 text-amber-600"
                              : isVideo
                                ? "bg-blue-50 text-blue-600"
                                : isPdf
                                  ? "bg-red-50 text-red-600"
                                  : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {isZip ? (
                            <Archive className="h-4 w-4" />
                          ) : isVideo ? (
                            <Film className="h-4 w-4" />
                          ) : (
                            <FileText className="h-4 w-4" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div
                            className="line-clamp-1 text-[12px] font-bold text-gray-800"
                            title={job.fileName}
                          >
                            {job.fileName}
                          </div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10px] font-medium text-gray-500">
                            <span>{formatDocFileSize(job.size)}</span>
                            <span>•</span>
                            <span
                              className={
                                job.status === "done"
                                  ? "font-semibold text-emerald-600"
                                  : job.status === "error"
                                    ? "font-semibold text-red-600"
                                    : "text-gray-600"
                              }
                            >
                              {job.status === "queued"
                                ? "Chờ xử lý"
                                : job.status === "uploading"
                                  ? (job.phase === "server" ? "Đang lưu..." : "Đang tải")
                                  : job.status === "done"
                                    ? "Hoàn tất"
                                    : "Lỗi"}
                            </span>
                            {job.status === "uploading" && (job.speedText || job.etaText) ? (
                              <>
                                <span>•</span>
                                <span>{job.speedText}</span>
                                {job.etaText ? <span>({job.etaText})</span> : null}
                              </>
                            ) : null}
                          </div>
                          {job.errorMessage ? (
                            <div className="mt-1 text-[10px] font-semibold text-red-600">
                              {job.errorMessage}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Nút hành động hoặc tiến độ % */}
                      <div className="flex shrink-0 items-center gap-2">
                        {job.status === "uploading" || job.status === "queued" ? (
                          <>
                            <span className="text-[11px] font-black text-gray-700">
                              {job.percent}%
                            </span>
                            <button
                              type="button"
                              onClick={() => cancelDocumentUpload(job.id)}
                              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
                              title="Hủy tệp này"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </>
                        ) : job.status === "done" ? (
                          <div className="flex items-center gap-1.5 text-emerald-600">
                            <CheckCircleIcon className="h-4 w-4" />
                            <button
                              type="button"
                              onClick={() => removeDocumentUploadJob(job.id)}
                              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                              title="Xóa khỏi danh sách"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-red-500">
                            <AlertCircleIcon className="h-4 w-4" />
                            <button
                              type="button"
                              onClick={() => removeDocumentUploadJob(job.id)}
                              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                              title="Xóa khỏi danh sách"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Thanh tiến độ */}
                    {job.status === "uploading" || (job.status === "queued" && job.percent > 0) ? (
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full bg-red-600 transition-all duration-200"
                          style={{ width: `${job.percent}%` }}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )
      ) : null}
    </div>
  );
};

function DocumentCard({ doc, onOpen }) {
  const visual = extVisual(doc.file_extension);
  const Icon = visual.Icon;
  const inactive = doc.is_active === false;

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className={`group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-3xl border transition-all duration-300 ${
        inactive
          ? "border-stone-200 bg-white/70 opacity-70 shadow-sm"
          : "border-red-100/90 bg-white shadow-md shadow-red-200/25 hover:-translate-y-1 hover:border-red-200 hover:shadow-xl hover:shadow-red-200/35"
      }`}
    >
      <div className="h-1.5 w-full bg-gradient-to-r from-red-600 via-rose-500 to-amber-400" />

      <div className="relative flex flex-1 gap-4 bg-gradient-to-br from-[#fff8f6] via-white to-amber-50/40 p-4 sm:p-5">
        <div
          className={`flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-2xl bg-gradient-to-br text-white shadow-md sm:h-[4.5rem] sm:w-[4.5rem] ${visual.wrap}`}
        >
          <Icon className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={2} />
          <span className="mt-1 text-[9px] font-black tracking-wide">
            {visual.label}
          </span>
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-2">
            <p className="line-clamp-1 h-4 text-[10px] font-bold uppercase tracking-wide text-red-700">
              {subjectLabel(doc)}
            </p>
            {inactive ? (
              <span className="shrink-0 rounded-md border border-red-100 bg-red-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-red-600">
                Ngưng
              </span>
            ) : null}
          </div>
          <h3 className="mt-1.5 line-clamp-2 min-h-[2.5rem] text-base font-extrabold leading-snug tracking-tight text-stone-900 transition-colors group-hover:text-red-950">
            {doc.file_name}
          </h3>
          <p
            className="mt-2 line-clamp-2 h-[2.75rem] text-sm leading-[1.375rem] text-stone-600"
            title={doc.description?.trim() || ""}
          >
            {doc.description?.trim() || "Không có mô tả."}
          </p>
        </div>
      </div>

      <div className="mt-auto flex items-center justify-between gap-2 border-t border-red-50/80 bg-gradient-to-b from-white to-[#fff5f2] px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-[11px] font-semibold text-stone-600">
            {courseMeta(doc)}
          </p>
          <p className="mt-0.5 truncate text-[10px] font-bold uppercase tracking-wide text-stone-400">
            {typeLabel(doc)}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-lg border px-2 py-1 text-[11px] font-bold tabular-nums ${visual.chip}`}
        >
          {formatDocFileSize(doc.file_size)}
        </span>
      </div>
    </article>
  );
}

function DocumentListRow({ doc, onOpen }) {
  const visual = extVisual(doc.file_extension);
  const Icon = visual.Icon;
  const inactive = doc.is_active === false;

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className={`flex cursor-pointer flex-col gap-3 rounded-2xl border bg-white p-3 shadow-sm transition-colors sm:flex-row sm:items-center sm:gap-4 sm:p-4 ${
        inactive
          ? "border-stone-200 opacity-70"
          : "border-red-100/90 hover:border-red-200 hover:shadow-md hover:shadow-red-100/30"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div
          className={`flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm ${visual.wrap}`}
        >
          <Icon className="h-5 w-5" strokeWidth={2.25} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="line-clamp-1 text-[10px] font-bold uppercase tracking-wide text-red-700">
              {subjectLabel(doc)}
            </p>
            <span
              className={`rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase ${visual.chip}`}
            >
              {visual.label}
            </span>
            {inactive ? (
              <span className="rounded border border-red-100 bg-red-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-red-600">
                Ngưng dùng
              </span>
            ) : null}
          </div>
          <h3 className="mt-0.5 line-clamp-1 text-sm font-extrabold text-stone-900 sm:text-base">
            {doc.file_name}
          </h3>
          <p
            className="mt-1 line-clamp-1 text-xs text-stone-600 sm:text-sm"
            title={doc.description?.trim() || ""}
          >
            {doc.description?.trim() || "—"}
          </p>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-3 sm:min-w-[200px] sm:justify-end">
        <div className="min-w-0 text-left sm:text-right">
          <p className="truncate text-[11px] font-semibold text-stone-600">
            {courseMeta(doc)}
          </p>
          <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wide text-stone-400">
            {typeLabel(doc)}
          </p>
        </div>
        <span className="shrink-0 rounded-lg border border-stone-200 bg-stone-50 px-2.5 py-1.5 text-[11px] font-bold tabular-nums text-stone-700">
          {formatDocFileSize(doc.file_size)}
        </span>
      </div>
    </article>
  );
}

export default DocumentCardPage;

function HeaderIconButton({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className="group relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-white text-red-700 shadow-sm transition-all hover:border-red-300 hover:bg-red-50"
    >
      <Icon className="h-5 w-5" strokeWidth={2.2} />
      <span className="pointer-events-none absolute bottom-full right-0 mb-2 translate-y-1 whitespace-nowrap rounded-lg border border-red-100 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 opacity-0 shadow-md transition-all group-hover:translate-y-0 group-hover:opacity-100">
        {label}
      </span>
    </button>
  );
}
